/**
 * The one way this app writes a CSV.
 *
 * There were two, and they disagreed on the thing that matters most here:
 * `BrokerImportPanel` prefixed a UTF-8 BOM, `exportMMM` did not. The file that
 * needs it is the one that carries campaign names — 65 of this account's 443
 * are Thai ("Ice Hockey - ประมวลภาพบรรยากาศในงาน") — and that was the file
 * without it, so Excel on Windows opened it as mojibake while the ASCII-only
 * import template opened fine.
 *
 * A CSV also leaves the app in a way a screen never does: it gets mailed,
 * opened in Excel, pasted into a deck. Whatever caveat sat next to the number
 * on the page stays behind. So a file written here can carry provenance lines
 * of its own, and callers that export a filtered range are expected to use them.
 *
 * @module services/csv
 */

/** A cell as callers hand it over; null/undefined render as an empty cell. */
export type CsvCell = string | number | boolean | null | undefined;

export interface CsvOptions {
  /**
   * Lines written above the header, each prefixed with `#`.
   *
   * For provenance: where the data came from, which range was asked for, which
   * range was actually served. Excel shows them as ordinary rows, which is the
   * point — someone reading only the file still learns what it is.
   */
  provenance?: string[];
}

/**
 * Excel and Sheets treat a leading =, +, - or @ as a formula, so a cell that
 * arrives from an ad platform can execute on open. Prefixing an apostrophe
 * neutralises it and is invisible in the cell — the standard mitigation.
 *
 * Applied to strings only: a negative number is not an injection.
 */
const neutraliseFormula = (value: string): string =>
  /^[=+\-@]/.test(value) ? `'${value}` : value;

/** RFC 4180: quote when the cell holds a delimiter, quote, or newline. */
const escapeCell = (cell: CsvCell): string => {
  if (cell === null || cell === undefined) return '';
  if (typeof cell === 'number') return Number.isFinite(cell) ? String(cell) : '';
  if (typeof cell === 'boolean') return cell ? 'true' : 'false';

  const text = neutraliseFormula(cell);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Rows to CSV text, with the byte-order mark Excel needs to read UTF-8.
 *
 * Without the BOM Excel guesses the code page and Thai text arrives as
 * mojibake. Every other reader (Sheets, pandas, Numbers) skips it.
 */
export const toCsv = (rows: CsvCell[][], options: CsvOptions = {}): string => {
  const provenance = (options.provenance ?? []).map(
    (line) => `# ${line.replace(/[\r\n]+/g, ' ')}`,
  );
  const body = rows.map((row) => row.map(escapeCell).join(',')).join('\r\n');
  return `\ufeff${[...provenance, body].filter(Boolean).join('\r\n')}`;
};

/**
 * Hand the browser a CSV to save.
 *
 * Separate from `toCsv` so the text can be built and asserted without a DOM;
 * every test in this repo exercises the text, not the anchor.
 */
export const downloadCsv = (filename: string, rows: CsvCell[][], options: CsvOptions = {}): void => {
  const blob = new Blob([toCsv(rows, options)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Without this the blob is held for the life of the document.
  URL.revokeObjectURL(url);
};
